import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { Button, Stack, Typography } from 'components';
import { Outlet } from 'react-router-dom';

import { CustomerAccountDetails } from './CustomerAccountDetails';
import { CustomerAccountsList } from './CustomerAccountsList';

import {
  Navigate,
  useMatch,
  useParams,
  useSelector,
  useTranslation,
} from 'third-party';

import { contractorsUrl } from 'constants/url';

import { useAdminCustomerAccountsList } from 'modules/adminCustomerAccountsList';

import { adminCustomerAccountsList } from 'selectors';

const Wrapper = styled(Stack)`
  flex-grow: 1;
  max-height: 100%;
`;

const Header = styled(Typography)`
  color: ${props => props.theme.custom.palette.secondary500};
  margin: 1rem 0 1rem 2rem;
  flex-shrink: 0;
  display: inline;
`;

const ContractorsListPanel = styled(Stack)`
  width: 42rem;
  border-right: 2px solid ${props => props.theme.custom.palette.muted100};
  flex-shrink: 0;
`;

const Separator = styled.div`
  position: relative;
  height: 0.1rem;
  background: ${props => props.theme.custom.palette.muted100};
  z-index: 2;
  flex-shrink: 0;
`;

const EmptyStateWrapper = styled(Stack)`
  gap: 1rem;
  max-width: 39rem;
  padding: 2.5rem 1.7rem;
  align-items: flex-start;
`;

const EmptyStateSubTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 1.8rem;
`;

const ContractorDetailsPanel = styled(Stack)`
  padding: 0.5rem;
  flex-grow: 1;
`;

const ButtonWrapper = styled(Stack)`
  padding-left: 1.47rem;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const CustomerAccountsCount = styled(Typography)`
  font-weight: 600;
  font-size: 1.8rem;
`;

export const AdminCustomerAccountViewWrapper = () => {
  const { resetCustomerAccountsList } = useAdminCustomerAccountsList();

  useEffect(() => {
    return () => {
      resetCustomerAccountsList();
    };
  }, [resetCustomerAccountsList]);
  return <Outlet />;
};

export const AdminCustomerAccountsView = () => {
  const { t } = useTranslation();
  const { contractorId } = useParams();
  const matchIndexRoute = useMatch(contractorsUrl());
  const firstAccountInList = useSelector(
    adminCustomerAccountsList.firstAccountInList,
  );
  const { loadCustomerAccounts, updateCustomerAccountsListFilters } =
    useAdminCustomerAccountsList();

  const accountsListCount = useSelector(
    adminCustomerAccountsList.customerAccountsListCount,
  );

  const customerAccountsListNameFilter = useSelector(
    adminCustomerAccountsList.customerAccountsListNameFilter,
  );

  const [showDeactivated, setShowDeactivated] = useState(false);

  const emptyState = useMemo(() => {
    return (
      <EmptyStateWrapper>
        <Typography variant="h5">
          {t('customers.table.emptyStateTitleProviders')}
        </Typography>
        <EmptyStateSubTitle variant="h6">
          {t('customers.table.emptyStateSubTitle')}
        </EmptyStateSubTitle>
      </EmptyStateWrapper>
    );
  }, [t]);

  const updateVisibilityFilter = useCallback(() => {
    const visibilityFilter = !showDeactivated;
    const filtersValue = {
      providerName: customerAccountsListNameFilter,
      includeDeactivated: visibilityFilter,
    };
    setShowDeactivated(visibilityFilter);
    updateCustomerAccountsListFilters(filtersValue);
    loadCustomerAccounts(filtersValue);
  }, [
    loadCustomerAccounts,
    showDeactivated,
    updateCustomerAccountsListFilters,
    customerAccountsListNameFilter,
  ]);

  return (
    <Wrapper direction="row">
      <ContractorsListPanel>
        <Stack
          direction="row"
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          <Header variant="h5">{t('customers.support.headerProviders')}</Header>
        </Stack>
        <ButtonWrapper direction="row">
          <CustomerAccountsCount>
            {t('customers.support.table.totalCount', {
              count: accountsListCount || 0,
            })}
          </CustomerAccountsCount>
          <Button id="customer-indicator" onClick={updateVisibilityFilter}>
            {showDeactivated ? (
              <>
                <VisibilityOutlinedIcon sx={{ marginRight: '1rem' }} />
                {t('customers.header.hideDeactivated')}
              </>
            ) : (
              <>
                <VisibilityOffOutlinedIcon sx={{ marginRight: '1rem' }} />
                {t('customers.header.showDeactivated')}
              </>
            )}
          </Button>
        </ButtonWrapper>
        <Separator />
        <CustomerAccountsList
          emptyState={emptyState}
          selectedAccountId={contractorId}
        />
      </ContractorsListPanel>
      <ContractorDetailsPanel>
        {contractorId && <CustomerAccountDetails />}
      </ContractorDetailsPanel>
      {matchIndexRoute && firstAccountInList && (
        <Navigate
          replace
          to={contractorsUrl(firstAccountInList.id.toString())}
        />
      )}
    </Wrapper>
  );
};
